import React, { useState, useEffect } from 'react';
import HeaderMain from "../components/header/HeaderMain";
import NavMain from "../components/nav/NavMain";
import axios from 'axios';
import assets from "../asset.json";
import liability from "../liability.json";
import equity from "../equity.json";
import GLOBALVAR from "../constants"; // Import GLOBALVAR constant
import { Table, Button } from "react-bootstrap";
import ToPeso from '../components/misc/ToPeso';
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { saveAs } from "file-saver";
import LoadingSpinner from '../components/misc/LoadingSpinner';

const jsonDataAssets = assets;
const jsonDataLiability = liability;
const jsonDataEquity = equity;

function BalanceSheet() {
    const [dateSelect, setDateSelect] = useState('');
    const [dateHeader, setDateHeader] = useState('');
    const [error, setError] = useState('');
    const [items, setItems] = useState([]);
    const [totalAssets, setTotalAssets] = useState(0);
    const [totalLiabilities, setTotalLiabilities] = useState(0);
    const [totalEquity, setTotalEquity] = useState(0);
    const [showSpinner, setShowSpinner] = useState(false);

    // Flatten the JSON data into a single array
    const flattenData = (data, type) => {
        const result = [];
        const traverse = (obj, path = '') => {
            if (Array.isArray(obj)) {
                obj.forEach(item => {
                    result.push({ ...item, type, path: `${path} > ${item.label}` });
                });
            } else if (typeof obj === 'object' && obj !== null) {
                Object.keys(obj).forEach(key => {
                    traverse(obj[key], `${path} > ${key}`);
                });
            }
        };
        traverse(data);
        return result;
    };

    const assetItems = flattenData(jsonDataAssets.Asset, 'Asset');
    const liabilityItems = flattenData(jsonDataLiability.Liability, 'Liability');
    const equityItems = flattenData(jsonDataEquity.Equity, 'Equity');
    const allItems = [...assetItems, ...liabilityItems, ...equityItems];

    useEffect(() => {
        if (items.length > 0) {
            const assets = items
                .filter(item => item.type === 'Asset')
                .reduce((acc, curr) => acc + parseFloat(curr.balance || 0), 0);
            const liabilities = items
                .filter(item => item.type === 'Liability')
                .reduce((acc, curr) => acc + parseFloat(curr.balance || 0), 0);
            const equity = items
                .filter(item => item.type === 'Equity')
                .reduce((acc, curr) => acc + parseFloat(curr.balance || 0), 0);

            // Ensure the totals are floats
            setTotalAssets(parseFloat(assets.toFixed(2))); // Round to 2 decimal places
            setTotalLiabilities(parseFloat(liabilities.toFixed(2))); // Round to 2 decimal places
            setTotalEquity(parseFloat(equity.toFixed(2))); // Round to 2 decimal places
        }
    }, [items]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setShowSpinner(true);
    
        if (!dateSelect) {
            setError('Select Date is required.');
            setShowSpinner(false);
            return;
        }
    
        try {
            setError('');
    
            // Fetch data for Assets, Liabilities, and Equity
            const fetchData = async () => {
                // Get item IDs for each type
                const assetItemIds = allItems
                    .filter(item => item.type === 'Asset')
                    .map(item => item.id)
                    .join(',');
    
                const liabilityItemIds = allItems
                    .filter(item => item.type === 'Liability')
                    .map(item => item.id)
                    .join(',');
    
                const equityItemIds = allItems
                    .filter(item => item.type === 'Equity')
                    .map(item => item.id)
                    .join(',');
    
                // Fetch data for Assets, Liabilities, and Equity in parallel
                const [assetResponse, liabilityResponse, equityResponse] = await Promise.all([
                    axios.get(`${GLOBALVAR["SERVER_ROOT"]}/api/asset-total/itemdate/multi`, {
                        params: { item_ids: assetItemIds, date: dateSelect }
                    }),
                    axios.get(`${GLOBALVAR["SERVER_ROOT"]}/api/liability-total/itemdate/multi`, {
                        params: { item_ids: liabilityItemIds, date: dateSelect }
                    }),
                    axios.get(`${GLOBALVAR["SERVER_ROOT"]}/api/equity-total/itemdate/multi`, {
                        params: { item_ids: equityItemIds, date: dateSelect }
                    })
                ]);
    
                // Create a map of backend responses for quick lookup
                const assetMap = new Map(assetResponse.data.map(record => [record.item_id, record.total]));
                const liabilityMap = new Map(liabilityResponse.data.map(record => [record.item_id, record.total]));
                const equityMap = new Map(equityResponse.data.map(record => [record.item_id, record.total]));
    
                // Combine all items with their balances
                const combinedRecords = allItems.map(item => {
                    const balance =
                        (item.type === 'Asset' && assetMap.get(item.id)) ||
                        (item.type === 'Liability' && liabilityMap.get(item.id)) ||
                        (item.type === 'Equity' && equityMap.get(item.id)) ||
                        0; // Default to 0 if no matching record is found
    
                    return {
                        id: item.id,
                        label: item.label || 'Unknown',
                        type: item.type,
                        path: item.path || '',
                        balance: balance
                    };
                });
    
                // Set the combined records to state
                setItems(combinedRecords);
    
                // Format the selected date for display
                const dateObj = new Date(dateSelect);
                const formattedDate = dateObj.toLocaleDateString('en-US', {
                    month: 'long',
                    day: '2-digit',
                    year: 'numeric'
                });
                setDateHeader(formattedDate);
            };
    
            await fetchData();
        } catch (err) {
            console.error("Error fetching data:", err);
            setError('Error fetching transactions. Please try again.');
        } finally {
            setShowSpinner(false);
        }
    };

    const handleExportPDF = async () => {
        setShowSpinner(true);

        try {
            const existingPdfBytes = await fetch("/pdftemplates/balance-sheet.pdf").then(res =>
                res.arrayBuffer()
            );

            const pdfDoc = await PDFDocument.load(existingPdfBytes);
            const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
            const helveticaFontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

            const pages = pdfDoc.getPages();
            const firstPage = pages[0];
            const secondPage = pages[1];
            const thirdPage = pages[2];
            const fourthPage = pages[3];

            const centerText = (text) => {
                const textWidth = helveticaFont.widthOfTextAtSize(text, 9);
                const pageWidth = firstPage.getWidth();
                return (pageWidth - textWidth) / 2;
            };

            const today = new Date();
            const dateTimeString = `${today.toLocaleString('default', { month: 'long' })} ${today.getDate()}, ${today.getFullYear()} ${today.getHours() % 12 || 12}:${today.getMinutes().toString().padStart(2, '0')}:${today.getSeconds().toString().padStart(2, '0')} ${today.getHours() >= 12 ? 'PM' : 'AM'}`;

            firstPage.drawText('As of', { x: centerText('As of'), y: 687, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
            firstPage.drawText(dateHeader, { x: centerText(dateHeader), y: 675, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });

            const coordY = {
                0:621, // first page first item
                2:583,
                10:456,//Honorarium Loan-Current
                24:266,//Honorarium Loan-Past Due
                38:75,//Honorarium Loan-Litigation
                41:704,//Pension Loan-Litigation
                52:551,//Allowance for Credit Losses on Loans and Receivables
                53:527,//Land
                61:412,//Accumulated Depreciation
                68:311,//Due From Head-Office
                69:286,//Accounts Receivable-Cash Advances
                80:692,//Interest Expense-Payable
                81:667,//Accounts Payable-Clients Fund
                93:501,//Dividends Payable
                94:476,//Bills Payable
                95:451,//Withholding Tax Payable-Expanded
                97:414,//Income Tax Payable
                98:389,//Value Added Tax Payable
                99:364,//Other Taxes and Licenses Payable
                100:339,//Unearned Interest and Discount-Loans and Receivable
                101:314,//Overages
                102:289,//Advances from Stockholders
                103:264,//Due to Head-Office
                104:239,//Miscellaneous Liabilities
                105:691,//Authorized Stocks
                107:654,//Capital-Others
                108:629,//Deposit for Future Subscription of Capital Stock
                109:604,//Undivided Profits
                110:579//Retained Earnings
            };

            if (items.length > 0) {
                let coordsFlag = 0;

                items.forEach((item, index) => {
                    const balCon = parseFloat(item.balance);
                    const balance = balCon.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                    if(index <= 40){
                        coordsFlag = (coordY[index])?coordY[index]:coordsFlag - 13;

                        firstPage.drawText(balance, { x: 458, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                    }

                    if(index > 40 && index <= 79){
                        coordsFlag = (coordY[index])?coordY[index]:coordsFlag - 13;

                        secondPage.drawText(balance, { x: 458, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                    }

                    if(index > 79 && index <= 104){
                        coordsFlag = (coordY[index])?coordY[index]:coordsFlag - 13;

                        thirdPage.drawText(balance, { x: 458, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                    }

                    if(index > 104 && index <= 110){
                        coordsFlag = (coordY[index])?coordY[index]:coordsFlag - 13;

                        fourthPage.drawText(balance, { x: 458, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                    }
                });

                fourthPage.drawText(dateTimeString, { x: 441, y: 412, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });

                fourthPage.drawText(totalAssets.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 169, y: 450, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                secondPage.drawText(totalAssets.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 458, y: 105, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                
                thirdPage.drawText("(" + totalLiabilities.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ")", { x: 458, y: 192, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                fourthPage.drawText("(" + totalEquity.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ")", { x: 458, y: 549, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });

                let liabquitotal = (totalLiabilities + totalEquity).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

                fourthPage.drawText("-" + liabquitotal, { x: 169, y: 437, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                
            }

            const pdfBytes = await pdfDoc.save();
            saveAs(new Blob([pdfBytes]), `Balance_Sheet_${Math.floor(new Date(dateHeader).getTime() / 1000)}.pdf`);
        } catch (err) {
            console.error("Error generating PDF:", err);
            setError('Error generating PDF. Please try again.');
        } finally {
            setShowSpinner(false);
        }
    };

    return (
        <>
            {showSpinner && <LoadingSpinner />}
            <NavMain slug="balance-sheet" parent="3" />
            <HeaderMain slug="balance-sheet" />
            <div className="main-content financials">
                <div className="container">
                    <h4>Retrieve Balance Sheet</h4>
                    <form onSubmit={handleSubmit} className="mb-4">
                        <div className="mb-3">
                            <label htmlFor="dateSelect" className="form-label">Select Date</label>
                            <input
                                type="date"
                                className="form-control"
                                id="dateSelect"
                                value={dateSelect}
                                onChange={(e) => setDateSelect(e.target.value)}
                                required
                            />
                        </div>
                        <button type="submit" className="btn btn-primary">Retrieve</button>
                    </form>
                    {error && <div className="alert alert-danger">{error}</div>}
                    <div className="date-selected">{dateHeader}</div>
                    {items.length > 0 && (
                        <>
                            <Table className="list-container table table-hover" responsive>
                                <thead>
                                    <tr>
                                        <th className='list-header-item'>Type of Account</th>
                                        <th className='list-header-item'>GL Account Description</th>
                                        <th className='list-header-item'>Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, index) => (
                                        <tr key={index} className='list-body'>
                                            <td className='list-body-item'>{item.type}</td>
                                            <td className='list-body-item'>{item.label}</td>
                                            <td className='list-body-item'><ToPeso amount={item.balance} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className='list-body'>
                                        <td className='list-body-item' colSpan="2">Total Assets</td>
                                        <td className='list-body-item'><ToPeso amount={totalAssets} /></td>
                                    </tr>
                                    <tr className='list-body'>
                                        <td className='list-body-item' colSpan="2">Total Liabilities and Equity</td>
                                        <td className='list-body-item'><ToPeso amount={totalLiabilities + totalEquity} /></td>
                                    </tr>
                                </tfoot>
                            </Table>
                            <Button variant="primary" onClick={handleExportPDF}>
                                Export PDF
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default BalanceSheet;